import { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'

import MiniHeader from '../../../../UI/MiniHeader'

import Branches from './components/Branch'
import BankData from './components/BankData'
import ShareAmounts from './components/ShareAmounts'
import MultipleComponent from './components/MultipleComponent'
import JuniorInsurance from './components/JuniorInsurance'
import ShareholderShares from './components/ShareholderShares'
import ReportsDeadLine from './components/ReportsDeadLine'
import RequirementsConfig from './components/RequirementsConfig'
import InvoicesOverdue from './components/InvoicesOverdue'
import ClearDB from './components/clearDB'
import SchoolYearReset from './components/SchoolYearReset'
import VisibilityAccess from '../../../VisibilityAccess'
import CompanyPresentationType from './components/CompanyPresentationType'
import ActivationRequirements from './components/ActivationRequirements'
import TeacherDocumentUpload from './components/TeacherDocumentUpload'
import HygieneTraining from './components/HygieneTraining'
import ConceptRequirement from './components/ConceptRequirement'
import DocumentTemplates from './components/DocumentTemplates'
import RequestChannel from './components/RequestChannel'
import Skills4RetailRequests from './components/Skills4RetailRequests'
import TicketsMenu from './components/TicketsMenu'
import OptionChanges from './components/OptionChanges'
import EmailDispatch from './components/EmailDispatch'
import LearningCourses from './components/LearningCourses'

import InvocingReportingReminders from './components/InvocingReportingReminders'
import CompanyReminders from './components/CompanyReminders'
import TicketTodoReminders from './components/TicketTodoReminders'
import FinancialReportingReminders from './components/FinancialReportingReminders'
import Rates from './components/Rates'

import { updateOptions } from '../../../../redux/ducks/options'

import http from '../../../../shared/http'
import { loadPrivateOptions } from '../../../../shared/helpers'
import { TApi, TRoles } from '../../../../shared/const'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import styles from './style.module.scss'
import Salutations from './components/Salutations'
import WorkAreas from './components/WorkAreas'
import TypeOfOffers from './components/TypeOfOffers'
import SupporterLookups from './components/SupporterLookups'

/*
 * Die Einstellungen waren eine einzige Seite von rund zwölftausend Pixeln
 * Länge: zwanzig Einstellungen hintereinander, getrennt nur durch eine
 * Haarlinie. Wer die Fälligkeit der Rechnungen ändern wollte, scrollte an
 * Anteilen, Anreden und Steuersätzen vorbei.
 *
 * Jetzt liegen sie in Gruppen hinter einem senkrechten Untermenü, das neben
 * dem Hauptmenü steht — dieselbe Bauart wie im Handbuch. Eine waagrechte
 * Reiterleiste trug die inzwischen neun Gruppen nicht mehr: Sie rutschten
 * seitlich weg und wurden übersehen. Senkrecht steht jede Gruppe von Anfang an
 * da, und unter der geöffneten stehen ihre Abschnitte als Sprungmarken.
 *
 * `sections` ist beides in einem: die Sprungmarken des Untermenüs und die
 * Zuordnung, in welcher Gruppe eine Marke liegt — eine Adresse mit #bankData
 * öffnet deshalb weiterhin von selbst die richtige Gruppe. `aliases` nimmt
 * Marken auf, die zu keinem eigenen Eintrag gehören (Umschläge, Unterpunkte).
 */
const GROUPS = [
	{
		key: 'masterData',
		title: 'Master data',
		intro: 'Auswahllisten, die in Formularen und Filtern der gesamten Anwendung auftauchen.',
		sections: [
			{ id: 'branch', title: 'Branches' },
			{ id: 'schoolTypes', title: 'School Types' },
			{ id: 'salutations', title: 'salutation' },
			{ id: 'workAreas', title: 'Function & working area' },
			{ id: 'typeOfOffers', title: 'Type of Offers' }
		]
	},
	{
		key: 'companies',
		title: 'Junior Companies',
		intro: 'Vorgaben für Anteile, Versicherung und die öffentliche Darstellung der Junior Companies.',
		sections: [
			{ id: 'shareholderShares', title: 'Shareholder Share for Junior' },
			{ id: 'shareAmounts', title: 'Max. Share Amounts' },
			{ id: 'juniorInsurance', title: 'Junior Insurance' },
			// Steht nur der Bundesadministration offen (VisibilityAccess unten) —
			// eine Sprungmarke ins Leere wäre ein Versprechen ohne Deckung.
			{
				id: 'companyPresentationType',
				title: 'Company Presentation Status',
				isVisible: ({ role }) => role === TRoles.FEDERAL_ADMIN
			}
		]
	},
	{
		key: 'activation',
		/*
		 * Hieß bis 25.08.2026 „Voraussetzungen für Aktivierung". Der Name stimmte
		 * nicht mehr: Das Skills4Retail-Konzept sperrt je nach Unternehmensform
		 * erst den ersten Finanzbericht, die Hygieneschulung läuft nach der
		 * Aktivierung weiter, und am Ende steht die Urkunde. Die Gruppe hält die
		 * Checkliste des ganzen Jahres, nicht die einer einzigen Hürde.
		 */
		title: 'Checklist & requirements',
		intro: 'Was eine Junior Company im Lauf des Jahres erfüllen muss — vor der Aktivierung, vor dem ersten Finanzbericht und bis zur Urkunde. Oben die Liste, wie die Schüler:innen sie sehen; darunter die Bedingungen, die sich zu- und abschalten lassen.',
		sections: [
			{ id: 'activationRequirements', title: 'Checklist as students see it' },
			{ id: 'activationRequirementsConfig', title: 'Configure requirements' },
			// Aendert keine Voraussetzung, sondern wer eine bestehende erfuellen
			// darf — deshalb ein eigener Abschnitt und keine Zeile in der Liste.
			{
				id: 'teacherDocumentUpload',
				title: 'Einverständniserklärung der Erziehungsberechtigten — Upload durch die Lehrkraft'
			},
			// Eigener Abschnitt, weil dahinter mehr steckt als ein Schalter:
			// Branchen, Abstand und Versandweg der Erinnerung.
			{ id: 'hygieneTraining', title: 'Hygiene- und Allergenschulung' },
			// Eigener Abschnitt wie die Hygieneschulung: Dahinter steckt mehr
			// als ein Schalter — eine Stelle je Unternehmensform, die Huerde der
			// Lernstrecke und der Abstand der Erinnerung.
			{ id: 'conceptRequirement', title: 'Skills4Retail-Konzept' }
		]
	},
	{
		/*
		 * Eine eigene Gruppe, weil sie zwei Dinge zusammenhält, die
		 * zusammengehören: ob es Kurse gibt und was ein Abschluss auf der
		 * Rechnung wert ist. Die Kurse selbst pflegt die Kursverwaltung.
		 * Nur Bundesadmins — es geht um Geld aller Bundesländer.
		 */
		key: 'courses',
		title: 'Courses & credit',
		intro: 'Kurse mit Videos, Quiz und Urkunde (Menüpunkt „Kurse"). Hier wird eingeschaltet, ob es sie gibt und was ein Abschluss je Unternehmensform auf der letzten Rechnung des Schuljahres gutschreibt.',
		sections: [{ id: 'learningCourses', title: 'Courses & credit' }],
		isVisible: ({ role }) => role === TRoles.FEDERAL_ADMIN
	},
	{
		key: 'taxes',
		title: 'Taxes',
		intro: 'Umsatzsteuersätze für Einnahmen und Ausgaben. Der angehakte Satz ist in den Berichten vorausgewählt.',
		sections: [
			{ id: 'vatRateRevenues', title: 'VAT Rates Revenues' },
			{ id: 'vatRateExpenditure', title: 'VAT Rates Expenditure' }
		]
	},
	{
		key: 'bank',
		title: 'Bank details',
		intro: 'Das Konto von Junior Achievement Austria. Es steht auf den Rechnungen an die Junior Companies.',
		sections: [{ id: 'bankData', title: 'Bank Data' }]
	},
	{
		key: 'reminders',
		title: 'Deadlines & reminders',
		intro: 'Fristen für die Berichtsabgabe, ab wann eine Rechnung als überfällig gilt und der Takt, in dem das System automatisch erinnert.',
		sections: [
			{ id: 'reportsDeadline', title: 'Reports deadline' },
			{ id: 'financialReportingReminders', title: 'Financial Reporting Reminders' },
			{ id: 'invoicesOverdue', title: 'Invoices overdue time' },
			{ id: 'invocingReportingReminders', title: 'Invoicing Reminders' },
			{ id: 'companyReminders', title: 'Company Reminders' },
			{ id: 'ticketTodoReminders', title: 'Erinnerung an offene Aufgaben' }
		]
	},
	{
		/*
		 * Eine eigene Gruppe für einen einzigen Knopf — mit Absicht. Er legt den
		 * Versand der ganzen Anwendung um; unter „Fristen & Erinnerungen“
		 * einsortiert sähe er aus wie eine Feinheit des Erinnerungswesens und
		 * wäre im Ernstfall nicht zu finden.
		 */
		key: 'email',
		title: 'E-Mail-Versand',
		intro: 'Wohin die Anwendung ihre E-Mails schickt. Im Normalfall an die Empfänger:innen. Für eine Probe lässt sich der Versand für 30 bis 90 Sekunden auf den Auffangdienst umlegen — danach stellt er sich von selbst zurück. Welche einzelne Vorlage überhaupt versendet wird, steht weiterhin im Menüpunkt „Kommunikation“.',
		sections: [{ id: 'emailDispatch', title: 'Testbetrieb auf Zeit' }]
	},
	{
		key: 'documents',
		// Kleingeschrieben, weil der vorhandene Schlüssel so heißt — wie bei den
		// Unterstützer:innen.
		title: 'documents',
		// Der Reiter stand bis 04.08.2026 als drittes Register im Bereich
		// Kommunikation. Das gehört in die Änderungsliste, nicht auf den
		// Bildschirm — deshalb steht es hier und nicht in der Einleitung.
		intro: 'Alle PDF-Dokumente, die die Anwendung erzeugt: Urkunden, Rechnungen, Bestätigungen. Zu jeder Vorlage lässt sich ansehen, wie sie mit echten Daten aussieht.',
		sections: [{ id: 'documentTemplates', title: 'Document templates' }]
	},
	{
		key: 'requests',
		title: 'Requests',
		intro: 'Der Hilfe-Knopf unten rechts: wer damit eine Anfrage an die zuständige Landesbetreuung stellen darf. Die Verwaltung ist davon nicht berührt — sie legt Anforderungen weiterhin im Menüpunkt „Anforderungen" an. Darunter steht, ob dieser Menüpunkt überhaupt angezeigt wird.',
		sections: [
			{ id: 'requestChannel', title: 'Wer darf eine Anfrage stellen?' },
			// Die Zuweisung schreibt eine Bundesadmin-Einstellung — wie der
			// Menüpunkt darunter nur für die Bundesadministration.
			{
				id: 'skills4RetailRequests',
				title: 'Responsible for Skills4Retail requests',
				isVisible: ({ role }) => role === TRoles.FEDERAL_ADMIN
			},
			// Kein fachlicher Wert, sondern eine Frage der Umgebung — deshalb
			// nur für die Bundesadministration.
			{
				id: 'ticketsMenu',
				title: 'Menüpunkt „Anforderungen & Anfragen" anzeigen',
				isVisible: ({ role }) => role === TRoles.FEDERAL_ADMIN
			}
		]
	},
	{
		key: 'supporters',
		title: 'supporters',
		intro: 'Diese Listen stehen Unterstützer:innen in ihrem Profil zur Auswahl und dienen Schulen als Filter. Änderungen wirken sofort. Bereits zugeordnete Einträge werden nicht gelöscht, sondern deaktiviert — die Zahl neben einem Eintrag zeigt, wie oft er verwendet wird. Schultypen und Branchen stehen bewusst nicht hier: Unterstützer:innen wählen aus denselben Listen, die auch beim Anlegen einer Schule bzw. einer Junior Company gelten (Gruppen „Schulen" und „Unternehmen").',
		sections: [
			{ id: 'supporter-competences', title: 'Kompetenzen' },
			{ id: 'supporter-support-categories', title: 'Kategorien des Unterstützungsbedarfs' },
			{ id: 'supporterRequestReminders', title: 'Erinnerungen an offene Betreuungsanfragen' },
			{ id: 'supporterContactFollowUp', title: 'Nach der Zusage: Kontaktaufnahme' },
			{ id: 'supporterApprovalReminders', title: 'Erinnerung an offene Freigaben' },
			{ id: 'supporterInvitationReminders', title: 'Erinnerung an offene Einladungen' }
		],
		// Der Umschlag um alle Listen; als Ziel taugt er, als Eintrag nicht.
		aliases: ['supporterLookups']
	},
	{
		/*
		 * Der Verlauf steht als eigene Gruppe und nicht in einer der anderen:
		 * Er gehört zu keiner einzelnen Einstellung, sondern zu allen. Ihn
		 * unter „System" zu legen wäre naheliegend, aber falsch — dort stehen
		 * Eingriffe, die nur die Bundesadministration sieht, und den Verlauf
		 * darf jede:r lesen, der die Einstellungen bearbeiten darf.
		 */
		key: 'changes',
		title: 'Änderungsverlauf',
		intro: 'Wer wann welche Einstellung geändert hat — und was die Anwendung daraufhin für die bestehenden Junior Companies getan hat. Eine Einstellung gilt für die ganze Umgebung; wird sie mitten im Schuljahr geändert, ändert sich damit für jede Junior Company etwas.',
		sections: [{ id: 'optionChanges', title: 'Letzte Änderungen' }]
	},
	{
		key: 'system',
		title: 'System',
		intro: 'Eingriffe, die den gesamten Datenbestand betreffen. Nur für die Bundesadministration.',
		sections: [
			// Die Ankündigung steht vor dem Reset: Erst wird angekündigt, dann
			// zurückgesetzt — in dieser Reihenfolge liest sich die Gruppe wie
			// der Ablauf, den sie beschreibt.
			{ id: 'schoolYearReset', title: 'Ankündigung des Jahresresets' },
			{ id: 'clearDB', title: 'Anonimize and clear db' }
		],
		// Ohne diesen Baustein bliebe die Gruppe leer — dann gibt es auch keinen
		// Eintrag dafür.
		//
		// Das Recht schaltet den Reset FREI. Die Verneinung an dieser Stelle
		// war ein Versehen (mit „add company email verification" eingeschleust,
		// 12.11.2025): Der Reset erschien genau bei denen, die ihn nicht haben
		// sollten, und fehlte den Berechtigten.
		isVisible: ({ role, permissions }) => role === TRoles.FEDERAL_ADMIN && Boolean(permissions?.manageDatabaseReset)
	}
]

// Alles, was in dieser Gruppe angesprungen werden kann — Einträge und Aliasse.
const anchorsOf = group => [...group.sections.map(s => s.id), ...(group.aliases || [])]

function Settings(props) {
	const permissions = props.permissions
	const { t } = useTranslation()
	/*
	 * Die Einstellungen der Verwaltung kommen angemeldet (`loadPrivateOptions`
	 * legt sie selbst in den Store). Nur wenn das Backend die Route noch nicht
	 * kennt, bleibt es beim alten Weg über die öffentliche Route.
	 */
	const fetchOptions = () => {
		loadPrivateOptions().then(data => {
			if (data) return
			http.get(TApi.COMMON_GET_OPTIONS).then(({ data }) => {
				props.updateOptions(data)
			})
		})
	}

	/*
	 * Was die Änderung für die bestehenden Junior Companies bedeutet — der
	 * Server rechnet es beim Speichern aus und schickt es als `effects` mit.
	 * Es steht unter der Kopfzeile, bis die nächste Einstellung gespeichert
	 * wird: Wer eine Voraussetzung einschaltet, soll im selben Moment lesen,
	 * dass aktivierte Junior Companies ihre Freischaltung behalten.
	 */
	const [wirkung, setWirkung] = useState([])

	const updateOptions = async form => {
		return http.put(TApi.ADMIN_OPTIONS, form).then(({ data }) => {
			// `effects` ist kein Einstellungswert und gehört nicht in den Store —
			// sonst stünde es als Option namens „effects" zwischen den echten.
			const { effects, ...werte } = data || {}

			props.updateOptions(werte)
			setWirkung(Array.isArray(effects) ? effects : [])
		})
	}

	useEffect(() => {
		fetchOptions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const history = useHistory()
	const { hash, pathname } = useLocation()

	const groups = useMemo(
		() => GROUPS.filter(g => !g.isVisible || g.isVisible({ role: props.role, permissions })),
		[props.role, permissions]
	)

	const [activeKey, setActiveKey] = useState(() => {
		const id = window.location.hash.replace('#', '')
		return GROUPS.find(g => anchorsOf(g).includes(id))?.key || GROUPS[0].key
	})

	/*
	 * Das Untermenü hebt hervor, bei welchem Abschnitt man gerade steht. Ohne
	 * das wäre es eine Liste von Sprungzielen, aber keine Ortsangabe: Wer eine
	 * lange Gruppe durchscrollt, wüsste nicht, wo er ist.
	 */
	const [currentId, setCurrentId] = useState(null)

	// Eine Sprungmarke aus einer Adresse öffnet zuerst die Gruppe …
	useEffect(() => {
		if (!hash) return
		const group = groups.find(g => anchorsOf(g).includes(hash.replace('#', '')))
		if (group) setActiveKey(group.key)
	}, [hash, groups])

	/*
	 * … und erst danach steht das Ziel im Baum und lässt sich anspringen.
	 *
	 * Ohne Animation: Ein sanfter Lauf braucht Einzelbilder — wo es die nicht
	 * gibt (eingebettete Ansichten, reduzierte Bewegung), bliebe die Seite
	 * stehen und die Sprungmarke täte scheinbar nichts.
	 *
	 * Mehrmals, weil manche Abschnitte ihre Listen erst vom Server holen
	 * (Unterstützer:innen, Dokumentvorlagen): Beim ersten Anlauf ist der Inhalt
	 * darüber noch kürzer, und der Sprung bliebe oberhalb des Ziels stehen.
	 * Nachgesteuert wird nur, solange niemand selbst gescrollt hat: Das erste
	 * Rad, Wischen oder Tastendrücken bricht die weiteren Anläufe ab, sonst
	 * risse das Nachsteuern die Seite unter der Hand zurück. Gemessen wird das
	 * an der Eingabe und nicht an der Bildlaufhöhe — die steht nach einem
	 * ausgelösten Sprung nicht sofort auf dem neuen Wert.
	 */
	useEffect(() => {
		if (!hash) return undefined
		const id = hash.replace('#', '')

		let cancelled = false
		const cancel = () => {
			cancelled = true
		}
		const jump = () => {
			if (!cancelled) document.getElementById(id)?.scrollIntoView({ block: 'start' })
		}

		const timers = [0, 400, 900].map(delay => setTimeout(jump, delay))
		window.addEventListener('wheel', cancel, { passive: true })
		window.addEventListener('touchstart', cancel, { passive: true })
		window.addEventListener('keydown', cancel)
		setCurrentId(id)

		return () => {
			timers.forEach(clearTimeout)
			window.removeEventListener('wheel', cancel)
			window.removeEventListener('touchstart', cancel)
			window.removeEventListener('keydown', cancel)
		}
	}, [hash, activeKey])

	/*
	 * Beim Gruppenwechsel an den Anfang — außer eine Sprungmarke ist im Spiel,
	 * dann gilt deren Ziel (der Effekt darüber).
	 */
	const navRef = useRef(null)
	useScrollTopOnTabChange(activeKey, navRef, !hash)

	const active = groups.find(g => g.key === activeKey) || groups[0]

	const sections = useMemo(
		() => active.sections.filter(s => !s.isVisible || s.isVisible({ role: props.role, permissions })),
		[active, props.role, permissions]
	)

	useEffect(() => {
		if (sections.length < 2) return undefined

		let frame = null
		const measure = () => {
			frame = null

			/*
			 * Am Ende der Seite gilt der letzte Abschnitt. Ein kurzer letzter
			 * Abschnitt kommt nie oben an — ohne diese Regel ließe er sich
			 * anspringen, aber nie hervorheben.
			 */
			const atBottom = window.innerHeight + window.scrollY >= document.body.scrollHeight - 2
			if (atBottom) return setCurrentId(sections[sections.length - 1].id)

			// Sonst der letzte Abschnitt, dessen Anfang schon oben vorbei ist —
			// knapp unterhalb der Kopfleiste, sonst gilt ein Abschnitt schon als
			// erreicht, während er noch unter ihr steckt.
			const passed = sections.filter(s => {
				const el = document.getElementById(s.id)
				return el && el.getBoundingClientRect().top <= 140
			})
			return setCurrentId(passed.length ? passed[passed.length - 1].id : sections[0].id)
		}
		const onScroll = () => {
			if (frame === null) frame = window.requestAnimationFrame(measure)
		}

		// Nachgemessen, sobald ein Sprung ausgeführt und nachgeladener Inhalt da
		// ist — sonst stünde die Hervorhebung beim ersten Abschnitt, während die
		// Seite längst beim angesprungenen steht.
		const timers = [setTimeout(measure, 0), setTimeout(measure, 450)]
		window.addEventListener('scroll', onScroll, { passive: true })
		return () => {
			timers.forEach(clearTimeout)
			window.removeEventListener('scroll', onScroll)
			if (frame !== null) window.cancelAnimationFrame(frame)
		}
	}, [sections])

	const selectGroup = key => {
		// Eine noch in der Adresse stehende Sprungmarke gehört zur vorigen Gruppe.
		if (hash) history.replace(pathname)
		setActiveKey(key)
	}

	const jumpTo = id => {
		if (hash) history.replace(pathname)
		document.getElementById(id)?.scrollIntoView({ block: 'start' })
		setCurrentId(id)
	}

	const data = [
		/* 	{ name: 'genders', title: t('gender') }, */
		// { name: 'branch', title: t('branch') },
		{
			name: 'schoolTypes',
			title: t('School Types'),
			hint: 'Schultypen zur Auswahl beim Anlegen einer Schule. Sie dienen als Filter in der Schulliste und als Zielgruppe im Modul Unterstützer:innen — dort wird bewusst dieselbe Liste verwendet, damit keine abweichenden Werte entstehen.',
			example: 'Beispiel: „AHS (Allgemein bildende höhere Schule)", „Berufsschule"'
		}
		// { name: 'salutations', title: t('salutation') },
		// { name: 'vatRateRevenues', title: t('VAT Rates Revenues'), number: true },
		// { name: 'vatRateExpenditure', title: t('VAT Rates Expenditure'), number: true },
		// { name: 'workAreas', title: t('Work Areas') },
		// { name: 'typeOfOffers', title: t('Type of Offers') }
	]

	return (
		<div className={styles.root}>
			<MiniHeader title={t('settings')} bg="#f8faff" />

			{/* Das Untermenü bleibt beim Scrollen stehen — sonst wären Gruppen- und
			    Abschnittswechsel am Ende einer langen Gruppe nur durch
			    Zurückscrollen erreichbar. */}
			<div className={styles.layout}>
				<nav className={styles.nav} ref={navRef} aria-label={t('Settings sections')}>
					{groups.map(group => {
						const isActive = group.key === active.key

						return (
							<div key={group.key}>
								<button
									type="button"
									aria-current={isActive ? 'true' : undefined}
									className={clsx(styles.navItem, isActive && styles.navItemActive)}
									onClick={() => selectGroup(group.key)}
								>
									{t(group.title)}
								</button>

								{/* Nur die geöffnete Gruppe zeigt ihre Abschnitte — und nur,
								    wenn es mehr als einen gibt: Eine Sprungmarke auf das
								    Einzige, was ohnehin dasteht, ist keine. */}
								{isActive && sections.length > 1 && (
									<div className={styles.navSections}>
										{sections.map(section => (
											<button
												key={section.id}
												type="button"
												aria-current={section.id === currentId ? 'true' : undefined}
												className={clsx(
													styles.navSection,
													section.id === currentId && styles.navSectionActive
												)}
												onClick={() => jumpTo(section.id)}
											>
												{t(section.title)}
											</button>
										))}
									</div>
								)}
							</div>
						)
					})}
				</nav>

				<div className={styles.content}>
					<h2 className={styles.groupTitle}>{t(active.title)}</h2>
					<p className={styles.intro}>{active.intro}</p>

					{/* Was die zuletzt gespeicherte Änderung für die bestehenden Junior
					    Companies bedeutet. Steht nur da, wenn sie etwas verschärft hat —
					    eine Lockerung wirkt für alle sofort und braucht keinen Hinweis. */}
					{wirkung.length > 0 && (
						<div className={styles.effects} role="status">
							<b>Gespeichert. Was das für die bestehenden Junior Companies heißt:</b>
							<ul>
								{wirkung.map((satz, index) => (
									<li key={index}>{satz}</li>
								))}
							</ul>
						</div>
					)}

					{active.key === 'masterData' && (
						<>
							<Branches form={props.options} updateOptions={updateOptions} />
							{data?.map(v => (
								<MultipleComponent
									key={v.name}
									name={v.name}
									title={v.title}
									number={v?.number}
									hint={v.hint}
									example={v.example}
									form={props.options}
									updateOptions={updateOptions}
								/>
							))}
							<Salutations form={props.options} updateOptions={updateOptions} />
							<WorkAreas form={props.options} updateOptions={updateOptions} />
							<TypeOfOffers form={props.options} updateOptions={updateOptions} />
						</>
					)}

					{active.key === 'companies' && (
						<>
							<ShareholderShares form={props.options} updateOptions={updateOptions} />
							<ShareAmounts form={props.options} updateOptions={updateOptions} />
							<JuniorInsurance form={props.options} updateOptions={updateOptions} />
							<VisibilityAccess access={[TRoles.FEDERAL_ADMIN]}>
								<CompanyPresentationType form={props.options} updateOptions={updateOptions} />
							</VisibilityAccess>
						</>
					)}

					{/*
					 * Erst die Vorschau der Checkliste, dann die Stellschraube dazu:
					 * Eine Bedingung abzuschalten heißt, einen Punkt aus dieser Liste
					 * zu nehmen — das ist nur nebeneinander zu sehen.
					 */}
					{active.key === 'activation' && (
						<>
							<ActivationRequirements form={props.options} />
							<RequirementsConfig form={props.options} updateOptions={updateOptions} />
							{/*
							 * Zwei Punkte der Liste sind persoenliche Nachweise
							 * einzelner Schueler:innen. Wer sie hochladen darf,
							 * steht direkt darunter — die Frage stellt sich erst,
							 * wenn man die Liste gesehen hat.
							 */}
							<TeacherDocumentUpload form={props.options} updateOptions={updateOptions} />
							{/*
							 * Zum Schluss die einzige Voraussetzung, die nicht für
							 * jede Junior Company gilt: Sie hängt an der Branche,
							 * und was dort einzustellen ist, steht in ihrer eigenen
							 * Karte.
							 */}
							<HygieneTraining form={props.options} updateOptions={updateOptions} />
							{/*
							 * Zuletzt das Skills4Retail-Konzept: die einzige
							 * Voraussetzung, die nicht nur an- und abgeschaltet
							 * wird, sondern je Unternehmensform an einer anderen
							 * Stelle im Jahr sperren kann.
							 */}
							<ConceptRequirement form={props.options} updateOptions={updateOptions} />
						</>
					)}

					{active.key === 'taxes' && (
						<>
							<Rates
								name="vatRateRevenues"
								title={t('VAT Rates Revenues')}
								hint="Umsatzsteuersätze zur Auswahl, wenn eine Junior Company eine Einnahme erfasst. Der Punkt links markiert den Satz, der dabei voreingestellt ist."
								example="Beispiel: 20, 10, 0"
								form={props.options}
								updateOptions={updateOptions}
							/>
							<Rates
								name="vatRateExpenditure"
								title={t('VAT Rates Expenditure')}
								hint="Umsatzsteuersätze zur Auswahl, wenn eine Junior Company eine Ausgabe erfasst. Der Punkt links markiert den Satz, der dabei voreingestellt ist."
								example="Beispiel: 20, 10, 0"
								form={props.options}
								updateOptions={updateOptions}
							/>
						</>
					)}

					{active.key === 'bank' && <BankData form={props.options} updateOptions={updateOptions} />}

					{active.key === 'courses' && (
						<VisibilityAccess access={[TRoles.FEDERAL_ADMIN]}>
							<LearningCourses form={props.options} updateOptions={updateOptions} />
						</VisibilityAccess>
					)}

					{active.key === 'changes' && <OptionChanges />}

					{active.key === 'requests' && (
						<>
							<RequestChannel form={props.options} updateOptions={updateOptions} />
							{/* Wie in den anderen Gruppen: die Sprungmarke oben
							    verspricht den Abschnitt nur der Rolle, die ihn
							    auch sehen darf. */}
							<VisibilityAccess access={[TRoles.FEDERAL_ADMIN]}>
								<Skills4RetailRequests form={props.options} updateOptions={updateOptions} />
							</VisibilityAccess>
							<VisibilityAccess access={[TRoles.FEDERAL_ADMIN]}>
								<TicketsMenu form={props.options} updateOptions={updateOptions} />
							</VisibilityAccess>
						</>
					)}

					{active.key === 'reminders' && (
						<>
							<ReportsDeadLine form={props.options} updateOptions={updateOptions} />
							<FinancialReportingReminders form={props.options} updateOptions={updateOptions} />
							{/* Die Fälligkeit steht vor den Erinnerungen zu Rechnungen: Sie
						    entscheidet, ab wann überhaupt erinnert wird. */}
							<InvoicesOverdue form={props.options} updateOptions={updateOptions} />
							<InvocingReportingReminders form={props.options} updateOptions={updateOptions} />
							<CompanyReminders form={props.options} updateOptions={updateOptions} />
							{/*
							 * Zum Schluss, weil es die einzige Erinnerung ist, die
							 * nicht an Junior Companies geht, sondern an die
							 * Administration selbst.
							 */}
							<TicketTodoReminders form={props.options} updateOptions={updateOptions} />
						</>
					)}

					{/*
					 * Nicht `updateOptions` von oben: Der Versandschalter spricht
					 * eine eigene Route an (der Endzeitpunkt gehört dem Server) und
					 * schiebt das Ergebnis danach selbst in den Store.
					 */}
					{active.key === 'email' && <EmailDispatch form={props.options} onOptions={props.updateOptions} />}

					{active.key === 'documents' && <DocumentTemplates />}

					{/*
					 * Abschnitt Unterstuetzer:innen. Die Stammdaten-Listen liegen in eigenen
					 * Tabellen, Kategorien und Erinnerungstakt dagegen in den Options -
					 * deshalb bekommt der Baustein beides mit.
					 */}
					{active.key === 'supporters' && (
						<SupporterLookups form={props.options} updateOptions={updateOptions} />
					)}

					{active.key === 'system' && (
						<VisibilityAccess access={[TRoles.FEDERAL_ADMIN]}>
							{/* Freigeschaltet durch das Recht, nicht gesperrt — siehe GROUPS. */}
							{permissions?.manageDatabaseReset && (
								<>
									<SchoolYearReset form={props.options} updateOptions={updateOptions} />
									<ClearDB updateOptions={updateOptions} options={props.options} />
								</>
							)}
						</VisibilityAccess>
					)}
				</div>
			</div>
		</div>
	)
}
Settings.propTypes = {
	options: PropTypes.object.isRequired,

	updateOptions: PropTypes.func.isRequired,
	permissions: PropTypes.object.isRequired,
	role: PropTypes.string
}
const mapStateToProps = state => ({
	options: state.options,
	permissions: state.user.profile.permissions,
	role: state.user.role
})
const mapDispatchToProps = dispatch => ({
	updateOptions: data => dispatch(updateOptions(data))
})
export default connect(mapStateToProps, mapDispatchToProps)(Settings)
